import { useCallback } from 'react';
import { Box } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import { ShowPageLayout } from 'framework/ui/layout';
import { FormFieldsData, FormShowTemplate } from 'framework/form';
import {
  APPROVAL_TYPE,
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  getProjectManager,
  ReportFrequency,
  useProjectDataContext,
  useNewProjectService,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import ProjectSection from 'domain/projects/newProject/shared/mainProjectSection/ProjectSection';
import DashboardBreadcrumbs from '../shared/breadcrumbs/DashboardBreadcrumbs';
import ProjectTabsMenu from '../shared/ProjectTabsMenu';
import ReuseDataLink from '../reuseData/ReuseDataLink';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import StartProjectReviewButton from '../projectReview/StartProjectReviewButton';

const ViewProjectLayout = () => {
  const {
    updateProject,
    updateProjectPartial,
    getProjectUpdatePartialInfoValidationSchema,
    getProjectUpdateFullInfoValidationSchema,
  } = useNewProjectService();
  const { data: project, refetch } = useProjectDataContext();
  const { isAccessGranted } = useProjectSecurityContext();
  const { isFeatureEnabled } = useFeatureToggle();
  const projectManager = getProjectManager(project);
  const location = useLocation();
  const isControlPlanesTab = location.pathname.includes('/controls');

  const submitFormFunc = async (data: FormFieldsData) => {
    const updatePayload = {
      name: data.name,
      alias: data.alias,
      description: data.description,
      engagementType: data.projectDimension?.engagementType,
      originalName: data.sensitiveData.originalName,
      isProjectForecastTrackingEnabled: data.settings.forecastSettings.isProjectForecastTrackingEnabled ?? false,
      isEvmTrackingEnabled: data.settings.forecastSettings.isEvmTrackingEnabled ?? false,
      externalResources: data.resourcesData.externalResources || [],
      reportFrequency: data.settings.reportSettings.reportFrequency,
      isSystemReportUsed: data.settings.reportSettings.isSystemReportUsed === 'true',
      approvalType:
        data.settings.reportSettings.isSystemReportUsed === 'false'
          ? APPROVAL_TYPE.NO_APPROVAL_REQUIRED
          : data.settings.reportSettings.approvalType,
      cycleFrequency: data.settings.cycleActivitySettings.cycleFrequency,
    };

    return updateProject(data.id, updatePayload);
  };

  const submitFormFuncPartial = useCallback(
    async (data: FormFieldsData) => {
      const updatePartialPayload = {
        description: data.description,
        externalResources: data.resourcesData.externalResources || [],
        reportFrequency: data.settings.reportSettings.reportFrequency,
        isSystemReportUsed: data.settings.reportSettings.isSystemReportUsed === 'true',
      };
      return updateProjectPartial(data.id, updatePartialPayload);
    },
    [updateProjectPartial],
  );

  return (
    <ShowPageLayout
      breadcrumbs={
        <DashboardBreadcrumbs
          useStaffingPath={!isAccessGranted(ACCESS_INFO_SECTIONS.BUSINESS_OUTCOME, ACCESS_INFO_ACTIONS.VIEW)}
        />
      }
      hasSeparateSections
    >
      <FormShowTemplate
        isCreateRequest={false}
        initialData={{
          ...project,
          projectManager,
          settings: {
            ...project.settings,
            reportSettings: {
              ...project.settings?.reportSettings,
              isSystemReportUsed: (project.settings?.reportSettings?.isSystemReportUsed ?? true).toString(),
              approvalType: project.settings?.reportSettings?.approvalType ?? APPROVAL_TYPE.MANUAL_APPROVAL,
              reportFrequency: project.settings?.reportSettings?.reportFrequency ?? ReportFrequency.WEEKLY,
            },
          },
        }}
        submitFormFunc={
          isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE)
            ? submitFormFunc
            : submitFormFuncPartial
        }
        getValidationSchemaFunc={
          isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_SUMMARY, ACCESS_INFO_ACTIONS.MANAGE)
            ? getProjectUpdateFullInfoValidationSchema
            : getProjectUpdatePartialInfoValidationSchema
        }
        afterSubmit={{
          message: 'You are awesome! The Project has been successfully updated.',
          execute: () => {
            refetch();
          },
        }}
      >
        <ProjectSection />
      </FormShowTemplate>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
        <Box flex={1} minWidth={0}>
          <ProjectTabsMenu />
        </Box>
        {isFeatureEnabled(Features.NEW_PROJECT_REVIEW) && isControlPlanesTab && (
          <Box>
            <StartProjectReviewButton />
          </Box>
        )}
        {isFeatureEnabled(Features.NEW_PROJECT_REUSE_DATA) && <ReuseDataLink />}
      </Box>
      <Box mt={2}>
        <Outlet />
      </Box>
    </ShowPageLayout>
  );
};

export default ViewProjectLayout;
